/**
 * The top bar: where you are, global search, the notification bell, the theme
 * toggle, and the account menu — and, during an impersonation, the way out.
 *
 * The search box is here rather than on the files screen on purpose — finding a
 * file by name is the single most common thing anyone does in a CMS, and burying
 * it one navigation deep makes the whole tree feel slower than it is.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import WidthNormalIcon from '@mui/icons-material/WidthNormal';
import WidthWideIcon from '@mui/icons-material/WidthWide';
import LogoutIcon from '@mui/icons-material/Logout';
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import MenuIcon from '@mui/icons-material/Menu';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import SearchIcon from '@mui/icons-material/Search';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { LanguageSelector } from '../components/LanguageSelector';
import { NotificationBell } from './NotificationBell';
import { Slot } from '../modules/Slot';
import { MIN_SEARCH_LENGTH } from '../api/resources';
import { useDebounced } from '../hooks/useDebounced';
import { useAuth } from '../auth/useAuth';
import { useColorMode } from '../theme/ColorModeProvider';
import { GLASS_RADIUS, SHELL_GAP, glass } from '../theme/theme';
import { MODULE_NAMESPACES } from '../modules/locales';
import type { NavSection } from './navigation';
import type { ContentWidth } from './useContentWidth';

export interface HeaderProps {
  /** Opens the temporary drawer. Only shown below the `md` breakpoint. */
  onOpenNavigation: () => void;
  /** The **key** for the section the current route sits in, from `navigation.ts`. */
  sectionKey?: NavSection['titleKey'] | undefined;
  /** Whether the content is held to the normal width or uses the whole screen. */
  contentWidth: ContentWidth;
  onToggleContentWidth: () => void;
}

/**
 * The height of both glass clusters in the bar, in pixels: the search box with
 * its padding. Every button in the right cluster is 40 px, the avatar included,
 * so they fit with room to spare.
 */
const CLUSTER_HEIGHT = 44;

/** "Ada Lovelace" → "AL". One letter is better than a wrong two. */
function initials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return (first + last).toUpperCase();
}

export function Header({ onOpenNavigation, sectionKey, contentWidth, onToggleContentWidth }: HeaderProps) {
  // Module namespaces too: a section a module declares is titled in its own.
  const { t } = useTranslation(['nav', 'common', ...MODULE_NAMESPACES]);
  const { user, isSuperadmin, impersonator, signOut, stopImpersonating } = useAuth();
  const { mode, toggle } = useColorMode();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [term, setTerm] = useState('');
  const debouncedTerm = useDebounced(term, 350);

  // Read through a ref, not a dependency. This effect *causes* the navigation
  // that changes `pathname`, so depending on it would re-run the effect on its
  // own result.
  const onSearchPage = useRef(false);
  onSearchPage.current = pathname === '/search';

  // Searching as you type. Without this the box looks live, does nothing until
  // Enter, and reads as broken — which is precisely how it was reported.
  useEffect(() => {
    const query = debouncedTerm.trim();
    // Shorter than the endpoint will answer: stay put rather than navigate to a
    // screen that can only tell you to keep typing.
    if (query.length < MIN_SEARCH_LENGTH) return;
    navigate(`/search?q=${encodeURIComponent(query)}`, {
      // Push on arrival so Back returns to the screen you were on; replace for
      // every keystroke after, so one search does not bury history under a
      // dozen entries you have to click through.
      replace: onSearchPage.current,
    });
  }, [debouncedTerm, navigate]);

  // Enter still submits, and does not wait out the debounce.
  function handleSearch(event: FormEvent) {
    event.preventDefault();
    const query = term.trim();
    if (query.length < MIN_SEARCH_LENGTH) return;
    navigate(`/search?q=${encodeURIComponent(query)}`, { replace: onSearchPage.current });
  }

  const [stopping, setStopping] = useState(false);

  // A full page load rather than a navigation, both ways: everything cached on
  // this page — polled counts, module state, loaded lists — belongs to the
  // person being impersonated. Back to their user screen, as GitLab does.
  async function handleStopImpersonating() {
    setStopping(true);
    const targetId = user?.id;
    try {
      await stopImpersonating();
      window.location.assign(targetId ? `/users/${targetId}` : '/');
    } catch {
      window.location.assign('/');
    }
  }

  async function handleSignOut() {
    setAnchor(null);
    await signOut();
    navigate('/login', { replace: true });
  }

  return (
    // Static and transparent: the frame is one screen tall and only the main
    // panel scrolls, so nothing passes under the bar. Only the two clusters
    // carry glass; the page background shows between them.
    <AppBar position="static" color="transparent" sx={{ flexShrink: 0 }}>
      {/* The same side gaps as the main panel, so both clusters line up with
          its edges: none on the left from md up, where the panel meets the
          sidebar. */}
      <Toolbar sx={{ gap: 1, pl: { xs: SHELL_GAP, md: 0 }, pr: { xs: SHELL_GAP } }}>
        <Box
          sx={[
            (theme) => glass(theme, 'panel'),
            {
              // Grows to its maximum first, and shrinks only once it would
              // touch the tools on the right — there is no spacer to share
              // the free space with.
              flex: '1 1 680px',
              minWidth: 0,
              maxWidth: 680,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 1.5,
              height: CLUSTER_HEIGHT,
              borderRadius: `${GLASS_RADIUS}px`,
            },
          ]}
        >
          <IconButton
            onClick={onOpenNavigation}
            sx={{ display: { md: 'none' } }}
            aria-label={t('nav:openNavigation')}
          >
            <MenuIcon />
          </IconButton>

          {sectionKey && (
            <Typography
              variant="subtitle2"
              color="text.secondary"
              noWrap
              sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 600, flexShrink: 0 }}
            >
              {t(sectionKey)}
            </Typography>
          )}

          <Box
            component="form"
            onSubmit={handleSearch}
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: 'action.hover',
            }}
          >
            {/* A real submit button, not a decorative glyph. The form has no
                other button, so implicit submission already handles Enter — but a
                magnifier that looks clickable and is not is the commonest way a
                search box appears broken. */}
            <IconButton
              type="submit"
              size="small"
              aria-label={t('common:action.search')}
              sx={{ p: 0.25, color: 'text.secondary' }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
            <InputBase
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder={t('nav:searchPlaceholder')}
              inputProps={{ 'aria-label': t('nav:searchPlaceholder') }}
              sx={{ flex: 1, fontSize: 14 }}
            />
          </Box>
        </Box>

        <Box
          sx={[
            (theme) => glass(theme, 'panel'),
            {
              ml: 'auto',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 0.5,
              height: CLUSTER_HEIGHT,
              borderRadius: `${GLASS_RADIUS}px`,
            },
          ]}
        >
          {/* Left of the language and theme controls: those are settings, these
              are things that change on their own and want to be noticed. Modules
              add theirs — the messaging badge — just before the bell. */}
          <Slot name="header.actions" props={{}} />
          <NotificationBell />

          <LanguageSelector />

          {/* Only from `lg` up: below it the content never reaches the normal
              limit, so the button would change nothing. */}
          <Tooltip title={contentWidth === 'wide' ? t('common:width.toNormal') : t('common:width.toWide')}>
            <IconButton
              onClick={onToggleContentWidth}
              aria-label={t('common:width.toggle')}
              aria-pressed={contentWidth === 'wide'}
              sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
            >
              {contentWidth === 'wide' ? <WidthNormalIcon /> : <WidthWideIcon />}
            </IconButton>
          </Tooltip>

          <Tooltip
            title={mode === 'dark' ? t('common:theme.toLight') : t('common:theme.toDark')}
          >
            <IconButton onClick={toggle} aria-label={t('common:theme.toggle')}>
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          {impersonator && (
            <Tooltip
              title={t('nav:impersonating', {
                name: user?.displayName ?? '',
                admin: impersonator.displayName,
              })}
            >
              <Button
                variant="contained"
                color="warning"
                size="small"
                onClick={() => void handleStopImpersonating()}
                disabled={stopping}
                startIcon={<NoAccountsIcon />}
                aria-label={t('nav:stopImpersonating')}
                sx={{
                  whiteSpace: 'nowrap',
                  // Icon only on a phone; the tooltip still says who is who.
                  minWidth: { xs: 40, sm: 64 },
                  '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {t('nav:stopImpersonating')}
                </Box>
              </Button>
            </Tooltip>
          )}

          <Tooltip title={user?.displayName ?? t('nav:account')}>
            <IconButton
              onClick={(event) => setAnchor(event.currentTarget)}
              aria-label={t('nav:accountMenu')}
              // 4 px around the 32 px avatar makes 40 px, as tall as every other
              // button here. The default 8 px would make the cluster taller.
              sx={{ p: 0.5 }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: 13,
                  fontWeight: 700,
                  // Warning while impersonating: the face is not your own.
                  bgcolor: impersonator ? 'warning.main' : isSuperadmin ? 'secondary.main' : 'primary.main',
                  color: impersonator
                    ? 'warning.contrastText'
                    : isSuperadmin
                      ? 'secondary.contrastText'
                      : 'primary.contrastText',
                }}
              >
                {initials(user?.displayName ?? '?')}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchor}
            open={anchor !== null}
            onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { minWidth: 240 } } }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" noWrap>
                {user?.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem component={RouterLink} to="/profile" onClick={() => setAnchor(null)}>
              <ListItemIcon>
                <PersonOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('nav:yourProfile')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleSignOut}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('nav:signOut')}</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
